import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import { FileUp } from 'lucide-react';
import { useFileDialog } from '@/hooks/useFileDialog';
import { isProFeature } from '@/lib/license';
import { CATEGORIES, localizedLabel, localizedToolTitle, localizedToolDescription } from '@/lib/toolCategories';
import { buildAllTools, openPdfForMode, resolveToolClick } from '@/lib/toolLaunch';

// ------------------------------------------------------------------
// Category-specific dropper copy
// ------------------------------------------------------------------
const DROPPER_COPY = {
  edit:     'Open a PDF to start editing',
  sign:     'Open a PDF to sign',
  esign:    'Open a PDF to send for signature',
  convert:  'Open a PDF to convert',
  organize: 'Open a PDF to organize',
};

function getDropperCopy(catId) {
  return DROPPER_COPY[catId] || 'Open a PDF';
}

// ------------------------------------------------------------------
// Convert category — "format rows" grouped view (Option B, 2026-07-28
// addendum). Pairs each format's from-PDF/to-PDF tools into one row with
// two direction pills, instead of ten flat ToolRow entries. Desktop
// CategoryLauncher's convert view ONLY — the web dashboard grid, command
// palette, mega-menus, breadcrumbs, and deep links are untouched.
// ------------------------------------------------------------------
const FORMAT_ROW_ORDER = ['word', 'excel', 'powerpoint', 'image'];
const FORMAT_LABELS = {
  word: { i18nKey: 'tools:convert.format_word', fallback: 'Word' },
  excel: { i18nKey: 'tools:convert.format_excel', fallback: 'Excel' },
  powerpoint: { i18nKey: 'tools:convert.format_powerpoint', fallback: 'PowerPoint' },
  image: { i18nKey: 'tools:convert.format_image', fallback: 'Images' },
};

function tOrFallback(t, key, fallback) {
  if (!key || !t) return fallback;
  const translated = t(key);
  return translated && translated !== key ? translated : fallback;
}

/**
 * Group the convert category's direction tools (formatGroup + direction
 * metadata added by the Option B addendum) into one row per format.
 */
function buildFormatRows(convertTools) {
  return FORMAT_ROW_ORDER.map((group) => {
    const groupTools = convertTools.filter((tool) => tool.formatGroup === group);
    const fromPdf = groupTools.find((tool) => tool.direction === 'from-pdf');
    const toPdf = groupTools.find((tool) => tool.direction === 'to-pdf');
    return {
      group,
      fromPdf,
      toPdf,
      icon: fromPdf?.icon || toPdf?.icon,
      colors: fromPdf?.colors || toPdf?.colors,
    };
  }).filter((row) => row.fromPdf || row.toPdf);
}

// ------------------------------------------------------------------
// CategoryLauncher
// ------------------------------------------------------------------
export default function CategoryLauncher() {
  const { cat } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openFile } = useFileDialog();

  // Resolve heading label
  const isAll = cat === 'all';
  const isConvert = cat === 'convert';
  const categoryEntry = isAll ? null : CATEGORIES.find((c) => c.id === cat);
  const headingLabel = isAll ? 'All tools' : (categoryEntry ? localizedLabel(t, categoryEntry) : cat);

  // Tool list — for 'all' show every tool; else filter to this category
  const allTools = buildAllTools();
  const tools = isAll
    ? allTools
    : allTools.filter((tool) => tool.tabId === cat);

  // Convert category (desktop launcher only, Option B addendum): group the
  // eight direction tools into four format rows instead of ten flat rows.
  const formatRows = isConvert ? buildFormatRows(tools) : [];
  const ocrTool = isConvert ? tools.find((tool) => tool.path === '/ocr') : null;

  // Dropper open handler — edit mode or default viewer
  const onOpenFile = useCallback(async (mode) => {
    const r = await openFile('.pdf');
    openPdfForMode(r, mode, navigate);
  }, [openFile, navigate]);

  // Category-specific mode for dropper
  const dropperMode = cat === 'edit' ? 'editText' : undefined;
  const dropperCopy = getDropperCopy(cat);

  return (
    <div style={{ padding: '28px 32px 36px' }}>
      {/* Heading */}
      <h2
        style={{
          fontSize: 20,
          fontWeight: 600,
          letterSpacing: '-0.3px',
          marginBottom: 20,
          color: 'var(--ds-ink, #191A24)',
        }}
      >
        {headingLabel}
      </h2>

      {/* Contextual dropper */}
      <div
        style={{
          border: '1.5px dashed var(--ds-line-3, #D2D3DD)',
          borderRadius: 12,
          background: 'var(--ds-surface-subtle, #FCFCFD)',
          padding: '28px 28px',
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          marginBottom: 28,
          maxWidth: 540,
        }}
      >
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 12,
            background: 'var(--ds-indigo-50, #EEF0FE)',
            color: 'var(--ds-indigo, #4F46E5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FileUp size={22} strokeWidth={1.5} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.1px', marginBottom: 3 }}>
            {dropperCopy}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ds-ink-3, #8C8FA0)' }}>
            Drag a file here, or click Open to browse.
          </div>
        </div>
        <button
          type="button"
          onClick={() => onOpenFile(dropperMode)}
          style={{
            height: 36,
            padding: '0 16px',
            borderRadius: 9,
            background: 'linear-gradient(140deg, var(--ds-indigo, #4F46E5), var(--ds-indigo-strong, #4338CA))',
            color: 'var(--ds-content, #fff)',
            border: 'none',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Open
        </button>
      </div>

      {/* Tool list */}
      {isConvert ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 640 }}>
          {formatRows.map((row) => (
            <FormatRow
              key={row.group}
              row={row}
              onOpenFile={onOpenFile}
              navigate={navigate}
              t={t}
            />
          ))}
          {ocrTool && (
            <ToolRow
              key={`${ocrTool.title}-${ocrTool.tabId}`}
              tool={ocrTool}
              onOpenFile={onOpenFile}
              navigate={navigate}
              t={t}
            />
          )}
        </div>
      ) : (
        tools.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 640 }}>
            {tools.map((tool) => (
              <ToolRow
                key={`${tool.title}-${tool.tabId}`}
                tool={tool}
                onOpenFile={onOpenFile}
                navigate={navigate}
                t={t}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// ToolRow — full-width row with icon + title + description
// ------------------------------------------------------------------
// Strips a trailing " PDF" for compact row titles ("Merge PDF" → "Merge",
// "Compress PDF" → "Compress"). Guarded against direction-tool titles
// like "Word to PDF": stripping there would leave a dangling "Word to" —
// never strip when the remainder ends with " to".
export function shortTitle(fullTitle) {
  if (!fullTitle) return fullTitle;
  const stripped = fullTitle.replace(/\s+PDF$/i, '');
  if (stripped !== fullTitle && /\sto$/i.test(stripped)) return fullTitle;
  return stripped;
}

function ToolRow({ tool, onOpenFile, navigate, t }) {
  const { icon: Icon, feature, pro, colors } = tool;
  const title = shortTitle(localizedToolTitle(t, tool));
  const description = localizedToolDescription(t, tool);
  const isPro = pro || (feature && isProFeature(feature));

  const handleClick = () => resolveToolClick(tool, { onOpenFile, navigate });

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '11px 14px',
        border: '1px solid var(--ds-line, #EAEBF0)',
        borderRadius: 10,
        background: 'var(--ds-content, #fff)',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        fontFamily: 'inherit',
        position: 'relative',
      }}
    >
      {/* Category-colored icon */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          background: colors?.bg || 'var(--ds-indigo-50, #EEF0FE)',
          color: colors?.fg || 'var(--ds-indigo, #4F46E5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {Icon && <Icon size={18} strokeWidth={1.5} />}
      </div>

      {/* Title + description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.1px', color: 'var(--ds-ink, #191A24)' }}>
          {title}
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--ds-ink-3, #8C8FA0)',
            marginTop: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {description}
        </div>
      </div>

      {/* PRO tag */}
      {isPro && (
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.4px',
            color: 'var(--ds-indigo-strong, #4338CA)',
            background: 'var(--ds-indigo-50, #EEF0FE)',
            border: '1px solid var(--ds-indigo-100, #E2E5FC)',
            padding: '1px 6px',
            borderRadius: 5,
            flexShrink: 0,
          }}
        >
          PRO
        </span>
      )}
    </button>
  );
}

// ------------------------------------------------------------------
// FormatRow — one row per convert format (Word/Excel/PowerPoint/Images):
// icon + localized format label, with a direction pill for each of the
// from-PDF/to-PDF tools that share that format.
// ------------------------------------------------------------------
function FormatRow({ row, onOpenFile, navigate, t }) {
  const { icon: Icon, colors, fromPdf, toPdf } = row;
  const labelMeta = FORMAT_LABELS[row.group];
  const label = tOrFallback(t, labelMeta?.i18nKey, labelMeta?.fallback || row.group);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '11px 14px',
        border: '1px solid var(--ds-line, #EAEBF0)',
        borderRadius: 10,
        background: 'var(--ds-content, #fff)',
      }}
    >
      {/* Format icon */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          background: colors?.bg || 'var(--ds-indigo-50, #EEF0FE)',
          color: colors?.fg || 'var(--ds-indigo, #4F46E5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {Icon && <Icon size={18} strokeWidth={1.5} />}
      </div>

      {/* Format label */}
      <div style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.1px', color: 'var(--ds-ink, #191A24)', flex: 1, minWidth: 0 }}>
        {label}
      </div>

      {/* Direction pills */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {fromPdf && <DirectionPill tool={fromPdf} onOpenFile={onOpenFile} navigate={navigate} t={t} />}
        {toPdf && <DirectionPill tool={toPdf} onOpenFile={onOpenFile} navigate={navigate} t={t} />}
      </div>
    </div>
  );
}

function DirectionPill({ tool, onOpenFile, navigate, t }) {
  const title = localizedToolTitle(t, tool);
  const handleClick = () => resolveToolClick(tool, { onOpenFile, navigate });

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        height: 30,
        padding: '0 12px',
        borderRadius: 7,
        border: '1px solid var(--ds-line-2, #DEDFE7)',
        background: 'var(--ds-surface-subtle, #FCFCFD)',
        color: 'var(--ds-ink-2, #565869)',
        fontFamily: 'inherit',
        fontSize: 12.5,
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {title}
    </button>
  );
}
